import { useEffect } from 'react';

import { type Cache, type Middleware, useSWRConfig } from 'swr';

import { StorageClient } from '@atlaskit/frontend-utilities/storage-client';

const STORAGE_KEY = 'POST_OFFICE__CLIENT_CACHE';
const CACHE_KEY = 'placement-api';
const CACHE_EXPIRY = 120000; // 2 minutes

const storageClient = new StorageClient(STORAGE_KEY);

/**
 * The cache that comes from swr only contains the data for the current request.
 * This function merges the cache with the existing cache from localStorage and writes the result to localStorage.
 * @param cache
 */
export const writeCacheToLocalStorage = (cache: Cache): void => {
	if (cache instanceof Map && cache.size > 0) {
		const existingCache = localStorageCacheProvider();
		const mergedCache = new Map([...existingCache, ...cache]);
		const appCache = Array.from(mergedCache.entries());

		// localStorage.getItem("POST_OFFICE__CLIENT_CACHE_placement-api")
		storageClient.setItemWithExpiry(CACHE_KEY, appCache, CACHE_EXPIRY);
	}
};

export const localStorageCacheProvider = () => {
	if (typeof window === 'undefined') {
		// Return a dummy storage for server-side rendering
		return new Map();
	}

	// When initializing, we restore the data from `localStorage` into a map.
	const cache = storageClient.getItem(CACHE_KEY) || [];

	return new Map(cache);
};

export const postOfficeCacheMiddleware: Middleware = (useSWRNext) => {
	return (key, fetcher, config) => {
		const { cache } = useSWRConfig();

		const swr = useSWRNext(key, fetcher, config);

		/**
		 * This will execute even when data is pulled from the local storage cache.
		 * This means that the cache will be updated and the CACHE_EXPIRY reset to be a rolling window.
		 *
		 * We may want to change this behaviour in the future to only update the cache when the data is NOT pulled from the local storage cache.
		 */
		useEffect(() => {
			if (swr.data !== undefined) {
				writeCacheToLocalStorage(cache);
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [swr.data]);

		return swr;
	};
};
